import { LoadingOutlined } from '@ant-design/icons';
import { T } from '@tolgee/react';
import { Spin } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CrossSvg } from '../../../../assets/icons';
import FolderSvg from '../../../../assets/icons/FolderSvg';
import theme from '../../../../assets/theme';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import useGoogleDrive from '../../../../hooks/useGoogleDrive';
import { useMetrics } from '../../../../hooks/useMetrics';
import {
  useParsePassageFile,
  useParsePassageFileUpload,
} from '../../../../hooks/useParsePassageFile';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import {
  DOWNLOAD_NOT_ALLOWED_KEY,
  DOWNLOAD_NOT_ALLOWED_MESSAGE,
  isDownloadNotAllowedError,
  useUploadGoogleDrive,
} from '../../../../hooks/useUploadGoogleDrive';
import { PromptInput } from '../../../../services/backendService/types';
import { MAX_LIMIT_PASSAGE } from '../../../../utils/aiGeneratorUtils';
import {
  isFileMimetypeSupported,
  SupportedMimeTypes,
  withResolvedMimeType,
} from '../../../../utils/mimetype';
import { isIos } from '../../../../utils/navigator';
import { TextAreaV2 } from '../../../_atoms/_TextArea/TextAreaV2';
import { useGoogleDrivePicker } from '../../../_atoms/GoogleDrivePicker';
import { CallbackDoc } from '../../../_atoms/GoogleDrivePicker/typeDefs';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { MaterialsMenu } from '../../../dynamicPages/CreatePage/intent-refinement/MaterialsMenu';
import {
  DOCUMENT_DRIVE_MIME_TYPES,
  exportFormatFor,
} from '../../../dynamicPages/CreatePage/intent-refinement/useGoogleDriveMaterialUpload';

/** Same ceiling the local-file path has always had. */
const MAX_FILE_SIZE_MB = 100;

/**
 * The passage field behind `upload_review_passage`.
 *
 * Two things change from v1. The paperclip becomes the same "Add Materials"
 * dropdown the intent-refinement composer uses, and a picked file is *parsed*
 * rather than handed to the generator as a path: it goes through
 * /processDocument and the extracted text lands in the textarea, where the
 * teacher can read it and fix it before generating. That widens the accepted
 * formats from pdf/jpeg/png to everything the parser reads — .docx, .doc,
 * .html, .txt, webp — and it means the generator only ever sees passage text.
 *
 * Google Drive comes through the same way. The picker offers what the
 * composer's does minus the decks — Google Docs, .docx, .doc, pdf and images —
 * the backend copies the file into the bucket, and the copy is parsed where it
 * lands, so the browser never sees those bytes either. A Google Doc has no
 * bytes to copy at all, so Drive exports it to PDF first.
 */
export const UploadPassageInputV2 = ({
  uploadedFile,
  onChange,
  label,
  promptInput,
  passageValue,
  setPassageValue,
  labelStyle,
  style,
}: {
  /**
   * Unused here — v2 never produces a file-shaped value, only passage text.
   * It stays in the shared prop type for v1.
   */
  onFileUploaded?: (uploadedFile: { filePath: string; fileType?: string }) => void;
  uploadedFile?: { filePath: string; fileType?: string } | undefined;
  onChange: (val: number | string | { filePath: string; fileType?: string }) => void;
  label?: string;
  promptInput: PromptInput;
  passageValue: { filePath: string; fileType?: string | undefined } | undefined;
  setPassageValue: React.Dispatch<
    React.SetStateAction<{ filePath: string; fileType?: string } | undefined>
  >;
  labelStyle?: React.CSSProperties;
  style?: React.CSSProperties;
}) => {
  const metrics = useMetrics();
  const tenantId = useRouterMatch().tenantId;
  const isDesktop = useBreakPoint(700);
  const textAreaRef = useRef<TextAreaRef>(null);
  const maxLength = promptInput.constraints?.maxLength || MAX_LIMIT_PASSAGE;

  const [notice, setNotice] = useState<PassageNotice | undefined>();
  const { parseFile, isParsing, reset: resetParse } = useParsePassageFile(tenantId ?? '');

  const { upload: copyFromDrive, isUploading: isCopyingFromDrive } = useUploadGoogleDrive(
    tenantId,
  );
  const {
    parseFileUpload,
    isParsing: isParsingDriveFile,
    reset: resetDriveParse,
  } = useParsePassageFileUpload();
  const { isReady: googleDriveIsReady, isError: googleDriveIsError } = useGoogleDrive(
    tenantId,
  );

  /**
   * The field stays editable while a file is being read, so what is in it when
   * the parse lands is not necessarily what was in it when the file was
   * picked. A ref, because the parse's success handler closes over this
   * component at pick time: reading `passageValue` from that closure would
   * append to — and so discard — whatever the teacher typed meanwhile.
   */
  const passageValueRef = useRef(passageValue);
  passageValueRef.current = passageValue;

  /**
   * Puts parsed text where the cursor would expect it: on its own in an empty
   * field, after what the teacher already wrote otherwise. Truncated to the
   * field's ceiling, and the caller is told when that happened so it can say
   * so rather than silently dropping the tail.
   */
  const appendPassageText = useCallback(
    (text: string) => {
      const existing = passageValueRef.current?.filePath ?? '';
      const combined = existing.trim()
        ? `${existing.trim()}\n\n${text.trim()}`
        : text.trim();
      const truncated = combined.slice(0, maxLength);
      setPassageValue({ filePath: truncated, fileType: undefined });
      onChange({ filePath: truncated, fileType: undefined });
      return { wasTruncated: truncated.length < combined.length };
    },
    [maxLength, onChange, setPassageValue],
  );

  const handleLocalFileSelected = useCallback(
    (picked: File) => {
      // iOS often reports no mime type at all; the name's extension stands in
      // so the file is judged — and uploaded — as what it actually is.
      const file = withResolvedMimeType(picked);
      const fileSizeInMb = file.size * 1e-6;
      const eventMetadata = {
        fileName: file.name,
        fileType: file.type,
        fileSize: fileSizeInMb,
        tenantId,
      };
      metrics.logEvent('Creator.Upload.Passage.Generator.Selected', eventMetadata);

      if (!isFileMimetypeSupported(file.type)) {
        setNotice({ type: 'unsupported' });
        return;
      }
      if (fileSizeInMb > MAX_FILE_SIZE_MB) {
        setNotice({ type: 'tooLarge' });
        return;
      }

      setNotice(undefined);
      parseFile(
        { file },
        {
          onSuccess: ({ text, fileName }) => {
            const { wasTruncated } = appendPassageText(text);
            setNotice({ type: 'parsed', fileName, wasTruncated });
            metrics.logEvent('Creator.Upload.Passage.Generator.Parsed', {
              ...eventMetadata,
              characterCount: text.length,
              wasTruncated,
            });
          },
          onError: error => {
            setNotice({
              type: 'parseFailed',
              message: (error as Error)?.message,
            });
            metrics.logEvent('UploadMaterials.Error', {
              ...eventMetadata,
              errorMessage: (error as Error)?.message,
            });
          },
        },
      );
    },
    [appendPassageText, metrics, parseFile, tenantId],
  );

  const handleGoogleDriveFileSelected = useCallback(
    (file: CallbackDoc) => {
      const fileSizeInMb = file.sizeBytes * 1e-6;
      const eventMetadata = {
        fileName: file.name,
        fileType: file.mimeType,
        // Google-native files report `sizeBytes: 0` — Drive stores nothing to
        // measure until it exports them.
        fileSize: fileSizeInMb,
        tenantId,
      };
      metrics.logEvent('LessonPage.Upload.GoogleDrive.Generator.Selected', eventMetadata);

      if (fileSizeInMb > MAX_FILE_SIZE_MB) {
        setNotice({ type: 'tooLarge' });
        return;
      }
      setNotice(undefined);

      // A Google Doc has no bytes of its own; Drive exports it to PDF, which
      // the parser reads like any other.
      const exportFormat = exportFormatFor(file.mimeType);

      copyFromDrive(
        { fileId: file.id, preferredMimeType: exportFormat },
        {
          onSuccess: ({ fileUploadId, filePath }) => {
            if (!fileUploadId) {
              setNotice({ type: 'parseFailed' });
              return;
            }
            parseFileUpload(
              {
                fileUpload: {
                  id: fileUploadId,
                  tenantId,
                  // The copy's bucket path is the file's id in the bucket —
                  // the same value the composer attaches as `fileId`.
                  fileId: filePath,
                  mimeType: exportFormat ? 'application/pdf' : file.mimeType,
                },
                fileName: file.name,
              },
              {
                onSuccess: ({ text, fileName }) => {
                  const { wasTruncated } = appendPassageText(text);
                  setNotice({ type: 'parsed', fileName, wasTruncated });
                  metrics.logEvent('Creator.Upload.Passage.Generator.Parsed', {
                    ...eventMetadata,
                    source: 'googleDrive',
                    characterCount: text.length,
                    wasTruncated,
                  });
                },
                onError: error => {
                  setNotice({
                    type: 'parseFailed',
                    message: (error as Error)?.message,
                  });
                  metrics.logEvent('UploadMaterials.Error', {
                    ...eventMetadata,
                    source: 'googleDrive',
                    errorMessage: (error as Error)?.message,
                  });
                },
              },
            );
          },
          onError: error => {
            setNotice({ type: 'parseFailed', message: (error as Error)?.message });
            metrics.logEvent('UploadMaterials.Error', {
              ...eventMetadata,
              source: 'googleDrive',
              errorMessage: (error as Error)?.message,
            });
          },
        },
      );
    },
    [appendPassageText, copyFromDrive, metrics, parseFileUpload, tenantId],
  );

  const { showFilePicker: showGoogleDrivePicker } = useGoogleDrivePicker(
    handleGoogleDriveFileSelected,
    tenantId,
    'DOCS',
    // The composer's list minus the decks: the passage field has one slot and
    // it holds text, so a .pptx has nowhere to go. PDFs stay.
    DOCUMENT_DRIVE_MIME_TYPES.join(','),
  );

  // A rendered input rather than one built by hand and kept in a ref: the
  // handler has to see the *current* passage text to append to it, and a
  // handler assigned once to a detached element keeps the text as it was the
  // first time the picker opened.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const showLocalFilePicker = useCallback(() => fileInputRef.current?.click(), []);

  const uploadedFileName = useMemo(() => {
    if (!uploadedFile?.filePath) return '';
    const separatorIndex = uploadedFile.filePath.indexOf('_');
    if (separatorIndex === -1) return uploadedFile.filePath;
    return uploadedFile.filePath.substring(separatorIndex + 1) || uploadedFile.filePath;
  }, [uploadedFile]);

  const handleClearUploadedFile = () => {
    resetDriveParse();
    resetParse();
    setNotice(undefined);
    onChange('');
  };

  // A Drive file takes the field over the way it does in v1: nothing was
  // parsed into text, so there is no text to show or edit.
  const isShowingFile = !!uploadedFile?.fileType;
  /** A file in flight takes the dropdown's slot, so the two can't overlap. */
  const isBusy = isParsing || isCopyingFromDrive || isParsingDriveFile;

  return (
    <>
      <div style={{ position: 'relative' }}>
        {!isShowingFile && (
          <TextAreaV2
            ref={textAreaRef}
            style={{ width: '100%' }}
            label={label ? label : 'Passage'}
            labelStyle={
              labelStyle ?? {
                fontSize: '18px',
                color: '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: 'Nunito',
                fontStyle: 'normal',
                fontWeight: 800,
                lineHeight: '140%',
              }
            }
            rows={3}
            placeholder={'Paste your passage, or add it from a file'}
            symbolCount={true}
            maxLength={maxLength}
            value={passageValue?.filePath as string | undefined}
            error={
              (passageValue?.filePath as string)?.length >= maxLength
                ? `${(passageValue?.filePath as string)?.length}/${maxLength}`
                : undefined
            }
            textAreaStyled={
              style ?? {
                minHeight: '100px',
                backgroundColor: 'transparent',
                color: 'white',
                paddingBottom: '45px',
              }
            }
            onChange={e => {
              setPassageValue({ filePath: e.target.value, fileType: undefined });
              onChange({ filePath: e.target.value, fileType: undefined });
            }}
          />
        )}

        {!isShowingFile && !isBusy && (
          <MenuContainer>
            <MaterialsMenu
              tenantId={tenantId}
              onClickUploadFile={showLocalFilePicker}
              onClickPasteText={() => textAreaRef.current?.focus()}
              // The passage field has no attachment list of its own — parsed
              // text is the only thing it keeps — so nothing arrives here.
              onFileUpload={() => undefined}
              onUploadFromGoogleDrive={showGoogleDrivePicker}
              isGoogleDriveReady={googleDriveIsReady}
              isGoogleDriveError={googleDriveIsError}
              // This component only renders behind `upload_review_passage`,
              // which has already decided the upload paths are on.
              fileOptionsEnabled
              eventMetadata={{ promptInputId: promptInput.id, tenantId }}
            />
          </MenuContainer>
        )}

        {isBusy && (
          <MenuContainer>
            <StatusRow>
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 20, color: theme.colors.orange, marginRight: 10 }}
                    spin
                  />
                }
              />
              {isCopyingFromDrive ? (
                <T
                  keyName="GeneratorPassage.FetchingFromDrive"
                  defaultValue="Getting your file from Drive..."
                />
              ) : (
                <T
                  keyName="GeneratorPassage.ReadingFile"
                  defaultValue="Reading your file..."
                />
              )}
            </StatusRow>
          </MenuContainer>
        )}

        {isShowingFile && (
          <FileChip
            style={{ width: isDesktop ? 200 : '100%', margin: isDesktop ? 0 : '10px 0' }}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <IconWrapper height="12px" width="12px" style={{ marginRight: 5 }}>
              <FolderSvg />
            </IconWrapper>
            <FileChipName style={{ maxWidth: isDesktop ? 200 : '100%' }}>
              {uploadedFileName}
            </FileChipName>
            <ClearFileButton
              height="8px"
              width="8px"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleClearUploadedFile();
              }}
            >
              <CrossSvg />
            </ClearFileButton>
          </FileChip>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        // iOS reports no mime type for a good deal of what it hands over, so
        // an accept list there hides files the parser would happily read.
        accept={isIos ? '*' : SupportedMimeTypes.join(',')}
        onChange={event => {
          const file = event.target.files?.[0];
          // Cleared so picking the same file twice still fires a change.
          event.target.value = '';
          if (file) handleLocalFileSelected(file);
        }}
      />

      {notice ? <PassageNoticeText notice={notice} /> : null}
    </>
  );
};

/** What the field says under itself after a file went through, or didn't. */
type PassageNotice =
  | { type: 'parsed'; fileName: string; wasTruncated: boolean }
  | { type: 'parseFailed'; message?: string }
  | { type: 'unsupported' }
  | { type: 'tooLarge' };

function PassageNoticeText({ notice }: { notice: PassageNotice }) {
  if (notice.type === 'parsed') {
    return (
      <NoticeText style={{ color: theme.colors.greyDark }}>
        {notice.wasTruncated ? (
          <T
            keyName="GeneratorPassage.ParsedTruncated"
            defaultValue="Added the text from {fileName} — it was longer than the passage field holds, so the end was left off. Check it reads the way you want."
            params={{ fileName: notice.fileName }}
          />
        ) : (
          <T
            keyName="GeneratorPassage.Parsed"
            defaultValue="Added the text from {fileName}. Check it reads the way you want before generating."
            params={{ fileName: notice.fileName }}
          />
        )}
      </NoticeText>
    );
  }

  if (notice.type === 'unsupported') {
    return (
      <NoticeText style={{ color: theme.colors.red }}>
        <T
          keyName="UseMyMaterials.Document.SelectFile.UnsupportedFileTypeError"
          defaultValue="Unsupported file type"
        />
      </NoticeText>
    );
  }

  if (notice.type === 'tooLarge') {
    return (
      <NoticeText style={{ color: theme.colors.red }}>
        <T
          keyName="GeneratorPassage.FileTooLarge"
          defaultValue="The file was too large"
        />
      </NoticeText>
    );
  }

  if (isDownloadNotAllowedError(notice)) {
    return (
      <NoticeText style={{ color: theme.colors.red }}>
        <T
          keyName={DOWNLOAD_NOT_ALLOWED_KEY}
          defaultValue={DOWNLOAD_NOT_ALLOWED_MESSAGE}
        />
      </NoticeText>
    );
  }

  return (
    <NoticeText style={{ color: theme.colors.red }}>
      {notice.message === 'Failed to find content in parsed file' ? (
        <T
          keyName="Materials.FailedToFindContent"
          defaultValue="Failed to find content in file. For best results, ensure your file contains clear, legible text."
        />
      ) : (
        <T
          keyName="Materials.FailedToProcessDocument"
          defaultValue="We couldn't process that file. It may be too large or corrupted. Please try again with a different file."
        />
      )}
    </NoticeText>
  );
}

const MenuContainer = styled.div`
  position: absolute;
  bottom: 4px;
  left: 4px;
  z-index: 100;
  /* The trigger floats over the text box, and a long passage scrolls under
     it — so it carries the field's own background rather than letting a line
     of the passage read through the button. */
  background: ${theme.colors.white};
  border-radius: 10px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  background: ${theme.colors.white};
  border-radius: 5px;
  padding: 9px 15px;
  position: relative;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
`;

const FileChip = styled.div`
  display: flex;
  align-items: center;
  background: ${theme.colors.paleBeige};
  border-radius: 5px;
  padding: 9px 15px;
  position: relative;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
`;

const FileChipName = styled.p`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0;
  margin-left: 5px;
`;

const ClearFileButton = styled(IconWrapper)`
  margin-bottom: 10px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  border-radius: 50%;
  padding: 5px;
  background: ${theme.colors.greyLight};
  position: absolute;
  right: -8px;
  top: -8px;
`;

const NoticeText = styled.div`
  margin-top: 8px;
  font-size: 14px;
  line-height: 140%;
`;
